import React, { createContext, useReducer } from "react";

interface CachedItem<T> extends EntityResult<T> {
  loading: boolean;
  updating?: boolean;
  updatingError?: Error;
}

interface assetPositionCacheState {
  [asset_position_id: string]: CachedItem<string[]>;
}

type CacheActions =
  | "ITEM_EXISTS"
  | "FETCHING_ITEM"
  | "FETCHED_ITEM"
  | "UPDATING_ITEM"
  | "UPDATED_ITEM";

export interface EntityResult<T> {
  data?: T;
  error?: Error | null;
}

interface AssetPositionCacheAction {
  type: CacheActions;
  asset_position_id: string;
  payload?: EntityResult<string[]>;
}

function updateStateForItem(
  state: assetPositionCacheState,
  asset_position_id: string,
  updates: Partial<CachedItem<string[]>>,
) {
  return {
    ...state,
    [asset_position_id]: {
      ...state[asset_position_id],
      ...updates,
    },
  };
}

const Reducer = (
  state: assetPositionCacheState,
  action: AssetPositionCacheAction,
): assetPositionCacheState => {
  switch (action.type) {
    case "ITEM_EXISTS":
      return updateStateForItem(state, action.asset_position_id, {
        loading: false,
      });
    case "FETCHING_ITEM":
      return updateStateForItem(state, action.asset_position_id, {
        loading: true,
      });
    case "FETCHED_ITEM":
      return updateStateForItem(state, action.asset_position_id, {
        ...action.payload,
        loading: false,
      });
    case "UPDATING_ITEM":
      return updateStateForItem(state, action.asset_position_id, {
        updating: true,
      });
    case "UPDATED_ITEM":
      return updateStateForItem(state, action.asset_position_id, {
        ...action.payload,
        updating: false,
      });
    default:
      return state;
  }
};

const AssetPositionCacheContext = createContext<{
  state: assetPositionCacheState;
  dispatch: React.Dispatch<AssetPositionCacheAction>;
}>({ state: {}, dispatch: () => null });

const AssetPositionCacheProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(Reducer, {});
  return (
    <AssetPositionCacheContext.Provider value={{ state, dispatch }}>
      {children}
    </AssetPositionCacheContext.Provider>
  );
};

function useGetCachedAssetPosition(
  asset_position_id: string,
): CachedItem<string[]> | undefined {
  const { state, dispatch } = React.useContext(AssetPositionCacheContext);
  const item = state[asset_position_id];
  if (!item) {
    dispatch({ type: "ITEM_EXISTS", asset_position_id });
  }
  return item;
}

export {
  AssetPositionCacheContext,
  Reducer,
  useGetCachedAssetPosition,
  AssetPositionCacheProvider,
};
